import React, { useEffect, useState } from 'react';
import { Bell, BellOff, Loader2, Volume2, CheckCircle2, ShieldAlert } from 'lucide-react';
import { Button } from './ui/button';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';

// urlBase64 → Uint8Array (VAPID public key)
const urlB64ToUint8 = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

const isSupported = () => typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

const PushNotifications = () => {
  const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [devices, setDevices] = useState(0);
  const [publicKey, setPublicKey] = useState('');

  const supported = isSupported();

  useEffect(() => {
    if (!supported) return;
    api.get('/push/vapid-public-key').then((r) => setPublicKey(r.data?.public_key || '')).catch(() => {});
    api.get('/push/status').then((r) => {
      setSubscribed(!!r.data?.subscribed);
      setDevices(r.data?.devices || 0);
    }).catch(() => {});
  }, [supported]);

  const enable = async () => {
    if (!supported) return;
    if (!publicKey) { toast({ title: 'Configuración pendiente', description: 'El servidor aún no envió la clave push.' }); return; }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        toast({ title: 'Permiso denegado', description: 'Puedes activarlo desde los ajustes del navegador.' });
        return;
      }

      // Ensure SW is registered (handles already-loaded pages too)
      if ('serviceWorker' in navigator) {
        const existing = await navigator.serviceWorker.getRegistration('/');
        if (!existing) {
          await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        }
      }

      // Wait until an active SW controls this page
      const reg = await navigator.serviceWorker.ready;

      // Clear any stale subscription (different VAPID key or expired endpoint)
      const oldSub = await reg.pushManager.getSubscription();
      if (oldSub) {
        try {
          // If the stored key differs from current VAPID key, force re-subscribe
          const storedKey = oldSub.options?.applicationServerKey;
          const newKeyBytes = urlB64ToUint8(publicKey);
          const mismatch = !storedKey || new Uint8Array(storedKey).length !== newKeyBytes.length ||
            [...new Uint8Array(storedKey)].some((b, i) => b !== newKeyBytes[i]);
          if (mismatch) {
            await oldSub.unsubscribe();
          }
        } catch { /* ignore unsubscribe errors */ }
      }

      const currentSub = await reg.pushManager.getSubscription();
      const sub = currentSub || await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8(publicKey),
      });

      const json = sub.toJSON();
      await api.post('/push/subscribe', { endpoint: json.endpoint, keys: json.keys });
      setSubscribed(true);
      setDevices((n) => n + (currentSub ? 0 : 1));
      toast({ title: '¡Notificaciones activadas!', description: 'Te avisaremos cuando cambie el estado de tu pedido.' });
      // Play in-app chime immediately as confirmation
      try { const a = new Audio('/sounds/notify.wav'); a.volume = 0.5; a.play().catch(() => {}); } catch {}
    } catch (e) {
      toast({ title: 'No se pudo activar', description: e?.message || 'Intenta de nuevo.' });
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await api.post('/push/unsubscribe', { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      setDevices(0);
      toast({ title: 'Notificaciones desactivadas' });
    } catch (e) {
      toast({ title: 'No se pudo desactivar', description: e?.message || 'Intenta de nuevo.' });
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      await api.post('/push/test', {});
      toast({ title: 'Enviamos una notificación de prueba', description: 'Debería llegar en unos segundos.' });
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Intenta de nuevo.' });
    } finally { setBusy(false); }
  };

  // -------- Unsupported / blocked states --------
  if (!supported) {
    return (
      <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5" data-testid="push-unsupported">
        <div className="flex items-center gap-2 mb-1">
          <BellOff className="w-4 h-4 text-white/60" />
          <h3 className="text-white font-bold">Notificaciones no disponibles</h3>
        </div>
        <p className="text-white/60 text-sm">Tu navegador no admite notificaciones push. Prueba desde Chrome, Edge o Firefox en Android o desde Safari en un iPhone con la app instalada.</p>
      </section>
    );
  }

  if (permission === 'denied') {
    return (
      <section className="bg-neutral-950 border border-white/10 rounded-2xl p-6 text-center max-w-xl mx-auto" data-testid="push-denied">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 rounded-full bg-rose-500/15 border border-rose-500/30 flex items-center justify-center mb-4">
            <ShieldAlert className="w-8 h-8 text-rose-500 animate-pulse" />
          </div>
          <h3 className="text-white font-black text-lg mb-2">¡Notificaciones Bloqueadas!</h3>
          <p className="text-rose-200/80 text-sm leading-relaxed mb-5">
            ¡Activa las notificaciones para no perderte nada! Así escucharás el sonido y recibirás el mensaje cuando tu pedido esté en proceso, en camino y entregado.
          </p>
          <div className="w-full bg-rose-500/5 border border-rose-500/20 rounded-xl p-4 text-left">
            <p className="text-xs font-bold text-rose-300 uppercase tracking-wider mb-1">¿Cómo activarlas?</p>
            <p className="text-xs text-white/70 leading-relaxed">
              Haz clic en el ícono del candado <span className="text-rose-400 font-bold">🔒</span> o el indicador de ajustes a la izquierda de la URL de tu navegador, cambia el permiso de Notificaciones a <span className="text-emerald-400 font-bold">Permitir</span> y vuelve a cargar esta página para disfrutar del seguimiento.
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (!subscribed) {
    return (
      <section className="bg-neutral-950 border border-white/10 rounded-2xl p-6 text-center max-w-xl mx-auto" data-testid="push-notifications-inactive">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 rounded-full bg-orange-500/15 border border-orange-500/30 flex items-center justify-center mb-4">
            <Bell className="w-8 h-8 text-orange-500 animate-pulse" />
          </div>
          <h3 className="text-white font-black text-lg mb-2">Notificaciones desactivadas</h3>
          <p className="text-orange-200/80 text-sm leading-relaxed mb-5">
            ¡Activa las notificaciones para no perderte nada! Así escucharás el sonido y recibirás el mensaje cuando tu pedido esté en proceso, en camino y entregado.
          </p>
          <div className="w-full bg-orange-500/5 border border-orange-500/20 rounded-xl p-4 text-center space-y-3">
            <p className="text-xs text-white/70 leading-relaxed">
              Haz clic en el botón de abajo para otorgar el permiso de notificaciones en tu navegador de forma segura.
            </p>
            <Button
              onClick={enable}
              disabled={busy}
              className="bg-orange-500 hover:bg-orange-600 text-black font-extrabold rounded-full px-6 py-2.5 h-auto text-sm w-full sm:w-auto"
              data-testid="push-enable-btn"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Activando...</> : <><Bell className="w-4 h-4 mr-1.5" /> Activar notificaciones</>}
            </Button>
          </div>
          <p className="mt-4 text-[11px] text-white/40 leading-snug">
            Tip: en iPhone las notificaciones sólo funcionan si primero instalaste NubesMTY como app desde el botón <em className="text-orange-300 not-italic">"Compartir → Agregar a inicio"</em> de Safari.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5" data-testid="push-notifications">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Bell className="w-4 h-4 text-orange-400" />
            <h3 className="text-white font-bold">Notificaciones de pedidos</h3>
          </div>
          <p className="text-white/70 text-sm leading-relaxed">
            ¡Tus notificaciones de compra están activadas! Recibirás alertas con sonido al instante para que puedas <strong className="text-white font-semibold">darle un seguimiento en tiempo real y tener una mejor visualización del progreso de tu pedido</strong> (desde que lo confirmamos, cuando va en camino, hasta que sea entregado o cancelado).
          </p>
        </div>
        <div className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider border ${subscribed ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-white/5 border-white/10 text-white/50'}`}>
          {subscribed ? 'Activas' : 'Inactivas'}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!subscribed ? (
          <Button
            onClick={enable}
            disabled={busy}
            className="bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-full"
            data-testid="push-enable-btn"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Activando...</> : <><Bell className="w-4 h-4 mr-1" /> Activar notificaciones</>}
          </Button>
        ) : (
          <>
            <Button
              onClick={sendTest}
              disabled={busy}
              className="!bg-transparent hover:!bg-white/5 border border-white/15 !text-white rounded-full"
              data-testid="push-test-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Volume2 className="w-4 h-4 mr-1" />}
              <span>Enviar prueba con sonido</span>
            </Button>
            <Button
              onClick={disable}
              disabled={busy}
              className="!bg-transparent hover:!bg-rose-500/10 border border-rose-500/30 !text-rose-300 rounded-full"
              data-testid="push-disable-btn"
            >
              <BellOff className="w-4 h-4 mr-1" />
              <span>Desactivar notificaciones</span>
            </Button>
          </>
        )}
      </div>

      {subscribed && (
        <p className="mt-3 text-[11px] text-emerald-300/80 inline-flex items-center gap-1.5">
          <CheckCircle2 className="w-3 h-3" /> Activas en {devices} dispositivo{devices === 1 ? '' : 's'} · sonido incluido
        </p>
      )}

      <p className="mt-4 text-[11px] text-white/40 leading-snug">
        Tip: en iPhone las notificaciones sólo funcionan si primero instalaste NubesMTY como app desde el botón <em className="text-orange-300 not-italic">"Compartir → Agregar a inicio"</em> de Safari.
      </p>
    </section>
  );
};

export default PushNotifications;
